import { lazy, Suspense, useSyncExternalStore, type ReactNode } from 'react'
import { ChartPlaceholder } from './PriceChart.styles'
import { DonutCentre, DonutLayer, DonutRoot } from './DonutChart.styles'

export interface DonutSlice {
  label: string
  value: number
  color: string
  /** Hover text; the label when absent. */
  tooltip?: string
}

export interface DonutChartProps {
  slices: DonutSlice[]
  /** Width and height, px. */
  size: number
  /** Shown in the hole; plain HTML, so it is server-rendered with the page. */
  centre?: ReactNode
  /** Label of the slice to pull out (the rest fade), e.g. while its legend row is hovered. */
  active?: string
  description?: string
}

// Highcharts needs a browser and is heavy: client only, after hydration (as PriceChart).
const DonutChartCanvas = lazy(() => import('./DonutChartCanvas'))

const subscribe = () => () => {}

/** A donut of slices with HTML in the hole. Same-size placeholder until Highcharts loads. */
export const DonutChart = ({ centre, ...props }: DonutChartProps) => {
  const isClient = useSyncExternalStore(
    subscribe,
    () => true,
    () => false,
  )
  const placeholder = <ChartPlaceholder style={{ height: props.size }} aria-hidden="true" />

  return (
    <DonutRoot style={{ width: props.size, height: props.size }}>
      {centre && <DonutCentre>{centre}</DonutCentre>}
      <DonutLayer>
        {isClient ? (
          <Suspense fallback={placeholder}>
            <DonutChartCanvas {...props} />
          </Suspense>
        ) : (
          placeholder
        )}
      </DonutLayer>
    </DonutRoot>
  )
}
